import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { measureBody } from './pageMeasure';
import { flattenedBodyIds } from './branchPagination';
import type { BranchPaginationContext } from './branchPagination';
import { planEditPages, pagePlansEqual, createPlanConvergenceGuard } from './pagePlan';
import type { PagedItem } from './pagePlan';
import type { ComposerNode } from '@safetyhub/shared';

/**
 * usePagedPlan — 낱장 배치 계획(`PagedItem[][]`)을 DOM 측정으로 수렴시키는 **낱장 래퍼 공용 파이프라인**.
 *
 * 낱장 래퍼는 **둘**이고(rc `PagedComposerView` = 상세·인쇄·PDF·공유·외부 서명 / admin `PagedEditBody`
 * = 편집 캔버스·팝오버·팝아웃) 2026-08-19 `/code-review-full`이 **그 둘이 이 배선을 거의 그대로 두 벌씩
 * 들고 있다**고 지적했다(state 초기화 · `pagePlanRef` 미러 · `convergenceRef` · body 리셋 effect ·
 * `measure()`(클립 리셋 + `planEditPages` + `shouldAccept`) · `measureRef` · 매 커밋 측정 effect ·
 * RO 구독 · `onPageCount` 통지 · `EPS`). 이 훅이 그 **한 벌**이고, 호스트에 남는 것은 JSX다.
 *
 * ⚠️ **왜 「사본이면 조용히 갈라진다」가 이 배선에 특히 참인가** — 이 파이프라인의 결함은 전부
 * **진동·고착** 형태로 나타난다(계획이 측정을 바꾸고 측정이 계획을 바꾼다). 그래서 한쪽만 고치면
 * 다른 쪽은 **크래시 없이 내용이 사라진 화면**을 낸다: 캔버스에서 branch가 사라지거나(아래 리셋 축
 * 주석), 통짜↔분할 진동으로 `Maximum update depth`가 나거나, stale 계획이 새 행을 클립한다.
 * 그 셋이 전부 **한쪽 호스트에서만** 발생한 전례가 있다.
 *
 * **이 훅이 소유하지 «않는» 것**(호스트가 각자 소유한다 — 합치면 틀린다):
 *   · `branchOpts` — 활성 슬롯 판정 입력. rc는 `{ data }`(조건 평가), admin은 `RenderTemplate` props
 *     그대로(탭 명시 선택 `activeBranchSlots`). **의도적으로 다르다.**
 *   · 낱장 치수(`pagePx`/`pageDimPx` — 같은 함수의 별칭)·`gap`·`marginLeft/Right`·`MarginCorners`·
 *     `PageNumberLabel` 호출부 — 전부 JSX 소유다. 특히 `PageNumberLabel` 호출부는 **두 호스트가
 *     글자 그대로 같아야** 하고 그것을 admin spec이 소스로 재므로(`pagedEditBodyPageNumber.test.tsx`
 *     `[되돌림] 호출부가 rc 래퍼와 글자 그대로 같다`), 반환값을 받을 때 **반드시 `pagePlan`이라는
 *     이름으로 구조분해**할 것. 이름을 바꾸면 그 가드가 red가 된다.
 *
 * **EPS는 `planEditPages`의 기본값(1)이 단독 소유한다.** 종전엔 두 호스트가 각자 `1`을 적어
 * (rc 모듈 상수 / admin `measure()` 지역 상수) 기본값과 합쳐 세 자리였다. 여기서 **세 번째 인자를
 * 넘기지 않는다** — 값이 같으므로(1 === 1) 동작은 불변이고 자리는 하나로 줄어든다.
 * ⚠️ `pagination.ts`의 `EPS = 0.5`는 **다른 것**이다(부동소수 비교 여유). 합치지 말 것.
 *
 * 측정 흐름: `measureBody`가 stale 계획 클립(`{ clipped: true }`)을 반환하면 통짜 계획으로 리셋해
 * 다음 커밋에서 전 행/항목이 렌더되게 하고(그때 재측정으로 정상 재분할), 정상 측정치면
 * `planEditPages`로 낱장 배치를 계산한다. `ResizeObserver`는 마운트 시 1회만 구독한다.
 *
 * ⚠️ **jsdom·`renderToStaticMarkup`에서는 layout effect가 돌지 않거나 `offsetHeight`가 0이라
 * 계획이 초기 통짜 1페이지에 머문다.** 그래서 「실제 분할」의 소유자는 이 훅의 spec이 아니라
 * `pagePlan`·`pageMeasure`·`repeatMultiBoundary`(실전 형상) spec과 **dev 실측**이다 — 이 훅의 초록을
 * 「낱장이 옳게 나뉜다」의 근거로 인용하지 말 것.
 */
export interface UsePagedPlanOptions {
  /** 계획·측정·slice가 공유하는 body(= `template.body`). 리셋 축의 한쪽이다(아래 `flatSig`). */
  body: ComposerNode[];
  /** 한 낱장의 본문 예산(px) — `pageContentHeight(template.page)`. */
  pageContentH: number;
  /**
   * 최상위 branch 투명 통과 입력. **호스트가 렌더(`RenderTemplate`)에 넘기는 것과 같은 값**이어야
   * 한다 — 갈라지면 계획의 nodeId 집합과 렌더 slice가 어긋나 branch가 화면에서 사라진다.
   * 어떤 훅 deps에도 안 들어가므로 호출측 memo는 얻는 게 없다(매 렌더 새 객체여도 무해).
   */
  branchOpts: BranchPaginationContext;
  /**
   * 낱장 배치 안정화 시 장수 통지. 소비처가 호스트마다 다르다 — rc는 PDF 캡처의
   * `print-meta[data-page-count]` 동기화, admin은 용지 설정 패널의 페이지번호 경고(「출력 시작이
   * 전체 장수보다 커서 아무 낱장에도 안 찍힌다」) 판정이다.
   */
  onPageCount?: (n: number) => void;
}

export interface UsePagedPlanResult {
  /** 측정 대상 컨테이너에 붙일 ref(호스트의 최상위 낱장 컨테이너 div). */
  containerRef: RefObject<HTMLDivElement>;
  /** 낱장별 배치. 측정 전엔 전 노드 통짜 1페이지, 측정 후 `planEditPages` 결과로 수렴한다. */
  pagePlan: PagedItem[][];
}

export function usePagedPlan({
  body,
  pageContentH,
  branchOpts,
  onPageCount,
}: UsePagedPlanOptions): UsePagedPlanResult {
  // 하강한 branch는 자기 대신 활성 슬롯 자식 id가 온다 — 계획의 **nodeId 집합**이 이 목록이다.
  const flatIds = flattenedBodyIds(body, branchOpts);
  // 초기 plan = 전 노드 통짜 1페이지(측정 전). 측정 후 planEditPages 결과로 수렴한다.
  const [pagePlan, setPagePlan] = useState<PagedItem[][]>(() => [
    flatIds.map((id) => ({ nodeId: id })),
  ]);
  // 현재 plan의 ref 미러 — 수렴 가드 판정을 setState updater 밖(순수 비교)에서 하기 위함
  // (updater 안에서 가드를 부르면 StrictMode 이중 호출로 히스토리가 오염된다).
  const pagePlanRef = useRef(pagePlan);
  // 측정 높이가 계획에 의존해 통짜↔분할이 서로를 유발하는 진동(rowspan 전피복 행 등) 차단
  // (2026-07-27 form-import 12x5 표 실사고 — Maximum update depth 크래시).
  const convergenceRef = useRef(createPlanConvergenceGuard());
  const containerRef = useRef<HTMLDivElement>(null);

  // body(내용) 변경 시 순환 히스토리 리셋 — undo 등으로 과거 계획과 같아지는 정당한 복귀를 막지 않게.
  // 선언 순서 중요: 아래 measure effect보다 먼저 실행돼야 새 내용 첫 측정이 리셋된 히스토리로 돈다.
  //
  // ⚠️ **리셋 축은 계획 입력 축과 같아야 한다.** branch 투명화 이후 계획의 nodeId 집합은 body뿐
  // 아니라 활성 슬롯 판정(data·editing·activeBranchSlots)에도 의존한다. body만 보면, 슬롯이 A→B→A로
  // 돌아왔을 때 가드가 "과거 계획과 같다"며 **정당한 재계획을 진동으로 오판해 거부**하고
  // (createPlanConvergenceGuard), 계획이 옛 슬롯에 고착된다. 그러면 buildSliceBody가 현재 ownership에
  // 없는 id를 버려 branch가 화면/캔버스에서 사라지고, 이후 매 측정이 같은 계획을 내 계속 거부되므로
  // **본문을 건드려 리셋될 때까지 지속**된다.
  const flatSig = flatIds.join('|');
  useLayoutEffect(() => {
    convergenceRef.current.reset();
  }, [body, flatSig]);

  // measure는 최신 body/pageContentH 클로저를 캡처해야 하므로 매 렌더 재생성하고, ResizeObserver는
  // measureRef 경유로 호출한다(RO는 마운트 시 1회만 구독 — 커밋마다 cleanup/재생성하면 초기 콜백으로
  // 측정이 2회 돈다).
  const measure = () => {
    const el = containerRef.current;
    if (!el) return;

    // DOM 측정(순수)을 rc 공유 함수(measureBody)에 위임 — data-composer-* 셀렉터로 body를 측정해
    // MeasuredNode[]를 얻는다. 측정 중 stale plan 클립(데이터 증가로 새 행/항목이 잘림)을 감지하면
    // { clipped: true }를 반환하므로, 그때만 여기서 plan을 통짜(rowRange/itemRange 없음)로 리셋해
    // 다음 커밋에서 전 행/항목이 렌더되게 한다(무한루프 없음: 통짜 렌더는 클립이 없어 다음
    // measure에서 clipped=false). 클립 리셋은 setState 의존이라 순수 함수 밖(여기)에 남긴다.
    const measured = measureBody(el, body, branchOpts);
    if (!Array.isArray(measured)) {
      // 통짜 리셋도 동등 비교 후에만 setState — 통짜 렌더에서도 클립이 지속되는 병리 데이터에서
      // 무한 setState(크래시)로 번지지 않게 한다(그 경우 통짜 상태로 조용히 정지).
      const whole: PagedItem[][] = [
        flattenedBodyIds(body, branchOpts).map((id) => ({ nodeId: id })),
      ];
      if (!pagePlansEqual(pagePlanRef.current, whole)) {
        pagePlanRef.current = whole;
        setPagePlan(whole);
      }
      return;
    }
    // 3번째 인자(EPS)를 넘기지 않는다 — 위 헤더의 「EPS는 planEditPages의 기본값이 단독 소유」.
    const plan = planEditPages(measured, pageContentH);
    if (convergenceRef.current.shouldAccept(pagePlanRef.current, plan)) {
      pagePlanRef.current = plan;
      setPagePlan(plan);
    }
  };

  const measureRef = useRef<() => void>();
  measureRef.current = measure;

  // 매 커밋 후 측정(deps 없음) — 배치 변화 시에만 setState(수렴). 진동(주기 순환)은 수렴 가드가 차단.
  useLayoutEffect(() => {
    measureRef.current?.();
  });

  // ResizeObserver는 마운트 시 1회만 구독 — 컨테이너 리사이즈(이미지 로딩 등) 시 재측정.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measureRef.current?.());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 낱장 배치가 안정화(pagePlan.length 확정)되면 장수를 통지한다. 배치가 안 바뀌면 setPagePlan을
  // 안 하므로 이 effect도 안 돈다.
  useEffect(() => {
    onPageCount?.(pagePlan.length);
  }, [pagePlan.length, onPageCount]);

  return { containerRef, pagePlan };
}
